import React from 'react';
import shallowEqual from 'react-pure-render/shallowEqual';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import cx from 'classnames';
import isEqual from 'lodash/isEqual';

import { PastesIcon } from '@edu-frontend/modules/src/features/pastesHistory';

import { Link, POPUP_THEMES, Text, withHookProps, Popup } from 'platform-components';
import { withRouterPath } from 'platform-components/hocs';
import { coursePropType, getColorScale, getStudentDisplayName } from 'platform-components/utils';
import { POPUP_DIRECTIONS } from 'platform-components/constants';

import { useShouldShowPastes } from 'common.components/teacher-detect-pastes';

import { CourseJournalLessonsCarousel } from '../__lessons-carousel/course-journal__lessons-carousel';
import Cell from '../__table-cell/course-journal__table-cell';
import COLORS from '../../../../../design/colors';

import './course-journal__table-body.scss';

const colorScaleEfficiency = getColorScale([0, 100], [COLORS.paleYellow, COLORS.moderateAquamarine], true);

function notNull(value) {
    return value !== null ? value : '\u00a0';
}

class CourseJournalTableBody extends React.Component {
    static propTypes = {
        course: coursePropType.isRequired,
        getRouterPath: PropTypes.func.isRequired,
        fieldToMod: PropTypes.object,
        fullNameWidth: PropTypes.string,
        hoveredColumn: PropTypes.string,
        journal: PropTypes.array.isRequired,
        ordering: PropTypes.shape({
            field: PropTypes.string,
            order: PropTypes.string,
        }).isRequired,
        toggleColumnHover: PropTypes.func.isRequired,
        shouldShowPastes: PropTypes.bool,
    };

    static contextTypes = {
        staticUrls: PropTypes.object.isRequired,
    };

    shouldComponentUpdate(nextProps) {
        return (
            nextProps.hoveredColumn !== this.props.hoveredColumn ||
            !shallowEqual(nextProps.ordering, this.props.ordering) ||
            !isEqual(nextProps.journal, this.props.journal) ||
            this.props.shouldShowPastes !== nextProps.shouldShowPastes
        );
    }

    getCellProps = (cellClassName, col) => {
        const { fieldToMod, hoveredColumn, ordering, toggleColumnHover } = this.props;
        let mix = fieldToMod[col];
        if (hoveredColumn === col) {
            mix = cx(mix, `${cellClassName}_hovered`);
        }
        // Если нет заховеренной колонки, и по текущей колонке отсортирована таблица.
        if (!hoveredColumn && ordering.field === col) {
            mix = cx(mix, `course-journal__table-column_ordering_${ordering.order}`);
        }
        return {
            mix,
            onMouseEnter: () => toggleColumnHover(col),
            onMouseLeave: () => toggleColumnHover(),
        };
    };

    renderStudentName = (student) => {
        const { getRouterPath, course } = this.props;
        const {
            staticUrls: { labStudentSubject },
        } = this.context;

        if (course.closed) {
            return <Text>{getStudentDisplayName(student)}</Text>;
        }

        const url = getRouterPath(labStudentSubject, {
            subject: course.subject,
            studentId: student.id,
        });

        return (
            <Link target="_blank" theme={Link.THEME.PRIMARY} to={url}>
                <Text>{getStudentDisplayName(student)}</Text>
            </Link>
        );
    };

    showDescription = (e) => {
        this.refs.cellPopup.setOwner(e.target);
        this.refs.cellPopup.show();
    };

    hideDescription = () => {
        this.refs.cellPopup.hide();
    };

    render() {
        const { journal, fullNameWidth, course, shouldShowPastes } = this.props;
        const cellProps = this.getCellProps.bind(this, 'course-journal__table-cell');

        /* eslint-disable complexity */
        return (
            <div className="course-journal__table-body">
                <div className="course-journal__table-row-delimiter">
                    <Cell style={{ width: fullNameWidth }} {...cellProps('student')} />
                    <Cell {...cellProps('lessons')} />
                    <Cell {...cellProps('total_efficiency')} />
                </div>
                <Popup
                    direction={POPUP_DIRECTIONS.TOP_RIGHT}
                    inLayer
                    mix={'course-journal__table-body__cheat-tooltip'}
                    offsetTop={10}
                    ref={'cellPopup'}
                    theme={POPUP_THEMES.DARK}
                >
                    <div className="course-journal__table-body__cheat-tooltip__container">
                        <Text id="courseJournal.cheatInfo.header" tagName="div" typography="paragraphXLMedium" />
                        <Text className="course-journal__table-body__cheat-tooltip__info" tagName="div">
                            <PastesIcon />
                            <Text id="courseJournal.cheatInfo.description" />
                        </Text>
                    </div>
                </Popup>
                {journal.map((row, i) => (
                    <div className="course-journal__table-row" key={i}>
                        <Cell style={{ width: fullNameWidth }} {...cellProps('student')}>
                            {this.renderStudentName(row.student)}
                        </Cell>
                        <Cell {...cellProps('lessons')}>
                            <CourseJournalLessonsCarousel
                                colorScale={colorScaleEfficiency}
                                courseId={course.id}
                                efficiencyByProblems={row.efficiencyByProblems}
                                hideDescription={this.hideDescription}
                                lessons={row.lessons}
                                ref={'cellPopup'}
                                showDescription={this.showDescription}
                                showPastes={shouldShowPastes}
                            />
                        </Cell>
                        <Cell {...cellProps('total_efficiency')}>
                            <Text>{notNull(row.total_efficiency)}</Text>
                        </Cell>
                    </div>
                ))}
            </div>
        );
        /* eslint-enable complexity */
    }
}

export default compose(
    withHookProps(useShouldShowPastes, {
        mapOwnProps: ({ course: { subject } }) => subject,
        hookPropKey: 'shouldShowPastes',
    }),
    withRouterPath
)(CourseJournalTableBody);
